import { FlowSummary, FlowType } from '@/lib/api/email-flows/types';

/**
 * Filter flows by type
 *
 * @param flows - Array of flow summaries
 * @param type - Flow type to filter by
 * @returns Filtered array of flows matching the specified type
 */
export function filterFlowsByType(flows: FlowSummary[], type: FlowType): FlowSummary[] {
    return flows.filter(flow => flow.type === type);
}

/**
 * Check if user has any flows
 *
 * @param flows - Array of flow summaries
 * @returns True if user has at least one flow
 */
export function hasFlows(flows: FlowSummary[]): boolean {
    return flows.length > 0;
}

/**
 * Check if user has any broadcasts
 *
 * @param broadcasts - Array of broadcast flow summaries
 * @returns True if user has at least one broadcast
 */
export function hasBroadcasts(broadcasts: FlowSummary[]): boolean {
    return broadcasts.length > 0;
}

/**
 * Get flow count by type
 *
 * @param flows - Array of flow summaries
 * @param type - Optional flow type to filter by
 * @returns Count of flows (filtered by type if specified)
 */
export function getFlowCount(flows: FlowSummary[], type?: FlowType): number {
    if (!type) return flows.length;
    return filterFlowsByType(flows, type).length;
}

/**
 * Parse Google Places address components into structured address data
 *
 * @param addressComponents - Array of address components from Google Places API
 * @returns Structured address object with street, city, state, postal_code, and country
 */
export function parseGooglePlaceAddress(
    addressComponents: Array<{
        long_name: string;
        short_name: string;
        types: string[];
    }>
): {
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
} {
    const getComponent = (type: string, useShortName = false) => {
        const component = addressComponents.find(c => c.types.includes(type));
        return useShortName ? component?.short_name || '' : component?.long_name || '';
    };

    const streetNumber = getComponent('street_number');
    const route = getComponent('route');
    const street = streetNumber ? `${streetNumber} ${route}` : route;

    return {
        street,
        city: getComponent('locality'),
        state: getComponent('administrative_area_level_1', true), // Use short_name for state (e.g., "CA" instead of "California")
        postal_code: getComponent('postal_code'),
        country: getComponent('country', true), // Use short_name for country (e.g., "US" instead of "United States")
    };
}

/**
 * Determine which empty state variant to show for flows
 *
 * @param isEmailFlowsAllowed - Whether user has access to email flows module
 * @returns Empty state variant ('upgrade' or 'add-flow')
 */
export function getFlowEmptyStateVariant(isEmailFlowsAllowed: boolean): 'upgrade' | 'add-flow' {
    return isEmailFlowsAllowed ? 'add-flow' : 'upgrade';
}

/**
 * Determine which empty state variant to show for broadcasts
 *
 * @param isEmailFlowsAllowed - Whether user has access to email flows module
 * @returns Empty state variant ('upgrade' or 'send-email')
 */
export function getBroadcastEmptyStateVariant(isEmailFlowsAllowed: boolean): 'upgrade' | 'send-email' {
    return isEmailFlowsAllowed ? 'send-email' : 'upgrade';
}
